import React, { useState, useEffect, useRef } from 'react';
import { X, SlidersHorizontal, Download, Eye, Check, Undo2 } from 'lucide-react';
import PaymentFilterPanel from '../components/common/PaymentFilterPanel';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Pagination from '../components/Pagination';
import CommonLoader from '../components/CommonLoader';
import ErrorPage from '../components/ErrorPage';
import { getPatienttsNameList, getPatientById } from '../redux/patientSlice';
import { filterAppointments } from '../redux/appointmentSlice';
import { setPaymentStatus } from '../redux/paymentSlice';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';
import { getDoctorMissingSetupItems } from '../utils/doctorSetupStatus';
import errorimg from '../assets/img/colorkit (1).svg';



export default function Payment() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation('paymentpage');
    const filterRef = useRef(null);
    const filterButtonRef = useRef(null);
    const isInitialCall = useRef(true);

    const { payments, paymentsMetadata, isLoading, error } = useSelector((state) => state.appointment);
    const { patientNameList, selectedPatient } = useSelector((state) => state.patient);
    const { selectedDoctor } = useSelector((state) => state.doctor);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchError, setSearchError] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [dateErrors, setDateErrors] = useState({ startDate: '', endDate: '' });
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        mode: [],
        status: [],
        patientId: [],
    });
    const [tempFilters, setTempFilters] = useState({
        startDate: '',
        endDate: '',
        mode: [],
        status: [],
        patientId: [],
    });
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
    const [patientOptions, setPatientOptions] = useState([]);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const axiosInstance = useAxiosAuth();


    const userRole = localStorage.getItem('Role');
    const Id = localStorage.getItem('ID');

    // Fetch patient data based on user role only on initial mount
    useEffect(() => {
        if (isInitialCall.current) {
            if (userRole === 'doctor') {
                dispatch(getPatienttsNameList(Id)); // Fetch patient name list for payments
            } else if (userRole === 'patient') {
                dispatch(getPatientById(Id));
            }
            isInitialCall.current = false;
        }
    }, [dispatch, userRole, Id]);

    // Set patient options for filtering
    useEffect(() => {
        if (userRole === 'doctor' && patientNameList.length > 0) {
            // Map patient name list to options format
            const options = patientNameList.map((patient) => ({
                id: patient.patientId,
                name: patient.name || ''
            }));
            setPatientOptions(options);
        } else if (userRole === 'patient' && selectedPatient) {
            setPatientOptions([{
                id: selectedPatient.patientId,
                name: `${selectedPatient.name || ''} ${selectedPatient.surname || ''}`.trim()
            }]);
        } else {
            setPatientOptions([]);
        }
    }, [patientNameList, selectedPatient, userRole]);

    // Fetch filtered appointments
    useEffect(() => {
        const queryParams = {
            appointmentStatus: 'Completed',
            ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate }),
            ...(filters.mode.length > 0 && { paymentMethod: filters.mode.join(',') }),
            ...(filters.patientId.length > 0 && userRole === 'doctor' && { patientId: filters.patientId.join(',') }),
            ...(userRole === 'patient' && { patientId: selectedPatient?.patientId }),
            ...(userRole === 'doctor' && { doctorId: Id }),
            page: currentPage,
            limit: itemsPerPage,
        };
        dispatch(filterAppointments(queryParams));
    }, [dispatch, filters, userRole, Id, selectedPatient, currentPage, itemsPerPage]);

    // Sync tempFilters with filters when filter popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
            setDateErrors({ startDate: '', endDate: '' });
        }
    }, [isFilterOpen]);

    // Handle click outside to close filter dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current && !filterRef.current.contains(event.target) &&
                filterButtonRef.current && !filterButtonRef.current.contains(event.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Helper function to get currency symbol
    // Handles both object { currencySymbol, currencyCode } (from API) and string codes (legacy)
    const getCurrencySymbol = (currency) => {
        if (!currency) return '';
        if (typeof currency === 'object') {
            return currency.currencySymbol || currency.currencyCode || '';
        }
        // Legacy string fallback
        if (typeof currency !== 'string') {
            return String(currency);
        }
        const upperCurrency = currency.toUpperCase();
        if (upperCurrency === 'INR') return '₹';
        if (upperCurrency === 'EUR') return '€';
        return currency;
    };

    // Map payments to a standardized format
    const mappedPayments = payments
        ?.filter((appt) => appt.payment)
        ?.map((appt, index) => ({
            id: index + 1,
            appointmentId: appt.appointmentId || t('paymentpage.na'),
            price: parseFloat(appt.price) || 0,
            pricetype: appt.pricetype || 'Type 1',
            currency: appt.currency || 'INR',
            mode: appt.payment?.method,
            status: appt.payment?.status?.toLowerCase().replace(/\s+/g, '') || 'notpaid',
            date: appt.payment?.date ? new Date(appt.payment.date).toISOString() : appt.scheduledDate || t('paymentpage.na'),
            patientId: appt.patientId || t('paymentpage.na'),
            specialtyId: appt.SpecialtyId || t('paymentpage.na'),
            addressType: appt.addressType || t('paymentpage.na'),
            addressTypeName: appt.addressTypeName || t('paymentpage.na'),
            admin: appt.admin || null,
            appointmentMode: appt.appointmentMode || t('paymentpage.na'),
            appointmentType: {
                typeId: appt.appointmentType || t('paymentpage.na'),
                type: appt.specialtyTypeName || t('paymentpage.na'),
                duration: appt.duration?.toString() || '0',
                price: appt.price || '0',
                specialtyId: appt.SpecialtyId || t('paymentpage.na'),
                virtual: appt.appointmentMode !== 'onsite',
            },
            description: appt.description || t('paymentpage.na'),
            doctor: { name: appt.doctorName || t('paymentpage.na'), specialty: [] },
            doctorDescription: appt.doctorDescription || null,
            doctorId: appt.doctorId || t('paymentpage.na'),
            doctorName: appt.doctorName || t('paymentpage.na'),
            duration: appt.duration || 0,
            keyword: appt.keyword || [],
            localTime: appt.localTime || t('paymentpage.na'),
            patient: {
                name: appt.patientName || t('paymentpage.na'),
                email: appt.patientEmail || t('paymentpage.na'),
                phone: appt.patientPhone || t('paymentpage.na'),
                surname: appt.patientSurname || t('paymentpage.na'),
            },
            patientName: appt.patientName || t('paymentpage.na'),
            patientSurname: appt.patientSurname || t('paymentpage.na'),
            photoUrls: appt.photoUrls || [],
            scheduledDate: appt.scheduledDate || t('paymentpage.na'),
            slotEndTime: appt.slotEndTime || t('paymentpage.na'),
            statusDetails: appt.status || t('paymentpage.na'),
            timeSlot: appt.timeSlot || t('paymentpage.na'),
            timeZone: appt.timeZone || t('paymentpage.na'),
        }));

    console.log('mappedPayments', mappedPayments);

    // Filter payments based on search term and filters
    const filteredPayments = mappedPayments.filter((payment) => {
        const matchesSearch = payment.appointmentId?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filters.status.length === 0 || filters.status.includes(payment.status);
        const matchesMode = filters.mode.length === 0 || filters.mode.includes(payment.mode);
        return matchesSearch && matchesStatus && matchesMode;
    });

    console.log('filteredPayments', filteredPayments);

    const handlePageChange = (page, newItemsPerPage) => {
        setCurrentPage(page);
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1); // Reset to page 1 when changing items per page
        }
    };

    // Maximum length for search inputs
    const MAX_SEARCH_LENGTH = 50;

    // Validate search input
    const validateSearchInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            return t('paymentpage.search_exceed_limit', { limit: MAX_SEARCH_LENGTH });
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('paymentpage.search_only_spaces');
        }

        return '';
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > MAX_SEARCH_LENGTH
            ? sanitized.substring(0, MAX_SEARCH_LENGTH)
            : sanitized;

        // Always update searchTerm so the input stays in sync
        setSearchTerm(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchError(error);
        setCurrentPage(1);
    };

    const handleApplyFilters = () => {
        // Validate dates before applying
        const today = new Date().toISOString().split('T')[0];
        const errors = { startDate: '', endDate: '' };
        if (tempFilters.startDate && tempFilters.endDate && new Date(tempFilters.startDate) > new Date(tempFilters.endDate)) {
            errors.startDate = t('paymentpage.start_date_after_end');
        }
        if (tempFilters.endDate && new Date(tempFilters.endDate) > new Date(today)) {
            errors.endDate = t('paymentpage.end_date_in_future');
        }
        if (errors.startDate || errors.endDate) {
            setDateErrors(errors);
            return;
        }

        setFilters(tempFilters);
        setCurrentPage(1);
        setIsFilterOpen(false);
        setDateErrors({ startDate: '', endDate: '' });
    };

    const clearFilters = () => {
        const emptyFilters = {
            startDate: '',
            endDate: '',
            mode: [],
            status: [],
            patientId: [],
        };
        setTempFilters(emptyFilters);
        setFilters(emptyFilters);
        setSearchTerm('');
        setSearchError('');
        setDateErrors({ startDate: '', endDate: '' });
        setCurrentPage(1);
        setIsFilterOpen(false);
    };

    const arraysShallowEqual = (a, b) => {
        if (a === b) return true;
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        if (a.length !== b.length) return false;
        return a.every((v, i) => v === b[i]);
    };

    const hasFilterChanges = () => {
        return (
            tempFilters.startDate !== filters.startDate ||
            tempFilters.endDate !== filters.endDate ||
            !arraysShallowEqual(tempFilters.mode, filters.mode) ||
            !arraysShallowEqual(tempFilters.status, filters.status) ||
            !arraysShallowEqual(tempFilters.patientId, filters.patientId)
        );
    };

    const formatDateTime = (localTime) => {
        if (!localTime) return { date: '', time: '' };
        const dateObj = new Date(localTime);
        const date = dateObj.toLocaleDateString('en-CA'); // Format as YYYY-MM-DD
        const time = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        return time;
    };

    const handleViewDetails = (payment) => {
        setSelectedPayment(payment);
        setIsDetailsModalOpen(true);
    };

    const handleViewReceipt = (payment) => {
        setSelectedPayment(payment);
        setIsReceiptModalOpen(true);
    };

    // Handle download Excel
    const handleDownload = async () => {
        try {
            setDownloadLoading(true);

            // Build query params exactly like the filterAppointments call
            const queryParams = {
                appointmentStatus: 'Completed',
                ...(filters.startDate && { startDate: filters.startDate }),
                ...(filters.endDate && { endDate: filters.endDate }),
                ...(filters.mode.length > 0 && { paymentMethod: filters.mode.join(',') }),
                ...(filters.patientId.length > 0 && userRole === 'doctor' && { patientId: filters.patientId.join(',') }),
                ...(userRole === 'patient' && { patientId: selectedPatient?.patientId }),
                ...(userRole === 'doctor' && { doctorId: Id }),
            };

            const response = await axiosInstance.get(`${baseURL}/appointment/payments/download-excel`, {
                params: queryParams,
                responseType: 'blob'
            });

            // Create blob URL and trigger download
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Generate filename with date range if available
            const dateRange = filters.startDate && filters.endDate
                ? `${filters.startDate}-to-${filters.endDate}`
                : new Date().toISOString().split('T')[0];
            link.download = `payments-${dateRange}.xlsx`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(t('paymentpage.download_success') || 'Excel downloaded successfully!');
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.warning(t('paymentpage.download_error') || 'Failed to download Excel');
        } finally {
            setDownloadLoading(false);
        }
    };

    // Phase-1 offline collection: mark a pending payment as collected.
    const refetchPayments = () => {
        dispatch(filterAppointments({
            appointmentStatus: 'Completed',
            ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate }),
            ...(filters.mode.length > 0 && { paymentMethod: filters.mode.join(',') }),
            ...(filters.patientId.length > 0 && userRole === 'doctor' && { patientId: filters.patientId.join(',') }),
            ...(userRole === 'patient' && { patientId: selectedPatient?.patientId }),
            ...(userRole === 'doctor' && { doctorId: Id }),
            page: currentPage,
            limit: itemsPerPage,
        }));
    };

    // A normalized row status counts as paid if it carries any of the values that
    // have ever meant paid (current 'paid' + legacy 'collected'/'completed').
    const isPaymentPaid = (status) => ['paid', 'collected', 'completed'].includes(status);

    // Bidirectional toggle: flip a row between Paid and Not-paid.
    const handleTogglePaid = async (payment, paid) => {
        try {
            await dispatch(setPaymentStatus({ appointmentId: payment.appointmentId, paid })).unwrap();
            toast.success(paid
                ? t('paymentpage.marked_collected', 'Payment marked as collected')
                : t('paymentpage.marked_not_paid', 'Payment marked as not paid'));
            refetchPayments();
        } catch (err) {
            toast.warn(typeof err === 'string' ? err : t('paymentpage.collect_failed', 'Failed to update payment'));
        }
    };

    // No need for client-side slicing as API returns paginated data
    const currentPayments = filteredPayments;

    console.log('currentPayments', currentPayments);

    // Handle error state — only blank the page when the initial fetch fails
    // (no data yet). Per-action failures shouldn't hijack the whole page.
    const hasNoPayments = !payments || payments.length === 0;
    if (error && hasNoPayments && !isLoading) {
        if (error?.status >= 500 && error?.status <= 599) {
            return <ErrorPage error={error} pageName={t('paymentpage.heading', 'Payments')} />;
        }
        if (error?.message === 'Network Error') {
            return <ErrorPage error={error} pageName={t('paymentpage.heading', 'Payments')} />;
        }
        return <ErrorPage error={error} pageName={t('paymentpage.heading', 'Payments')} />;
    }

    // Profile completion validation for doctors
    if (userRole === 'doctor') {
        // Get missing setup items using utility function
        const missingItemKeys = getDoctorMissingSetupItems(userRole, selectedDoctor);
        const missingItems = missingItemKeys.map(key => t(key));

        // If any required items are missing, show restriction page
        if (missingItems.length > 0) {
            return (
                <div className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
                    <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                        <img src={errorimg} alt={t('profileValidation.altText')} className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain" />
                        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">{t('profileValidation.title')}</h2>
                        <p className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
                            {t('profileValidation.message')}
                        </p>
                        <div className="text-left bg-red-50 border border-red-200 rounded-lg p-4 mb-4 sm:mb-6 md:mb-8">
                            <p className="text-xs sm:text-sm md:text-base text-gray-700 font-semibold mb-2">{t('profileValidation.missingItems')}:</p>
                            <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm md:text-base text-gray-600">
                                {missingItems.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </ul>
                        </div>
                        <button onClick={() => navigate('/doctorProfile')} className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:from-[#3aa8a8] hover:to-[#2a7b7b] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 cursor-pointer">
                            {t('profileValidation.goToProfile')}
                        </button>
                    </div>
                </div>
            );
        }
    }

    // Profile completion validation for patients
    if (userRole === 'patient' && selectedPatient && selectedPatient.profileStatus === 'incomplete' && !isLoading) {
        return (
            <div className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
                <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                    <img
                        src={errorimg}
                        alt={t('patientappointment.profile_incomplete_alt', 'Profile Incomplete')}
                        className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain"
                    />
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">
                        {t('patientappointment.unable_to_book', 'Unable to View Payments')}
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
                        {t('patientappointment.profile_incomplete_message', 'Your profile is incomplete. Please complete your profile to view payments.')}
                    </p>
                    <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 md:mb-8">
                        {t('patientappointment.update_profile_message', 'Please update your profile information to continue.')}
                    </p>
                    <button
                        onClick={() => navigate('/patientProfile')}
                        className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 cursor-pointer bg-[#4ABDBD] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:bg-[#3aa8a8] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50"
                    >
                        {t('patientappointment.go_to_profile', 'Go to Profile')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="payments-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* Header Row */}
            <div data-testid="payments-page-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="payments-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('paymentpage.heading')}
                </h1>
                <div className="flex items-center gap-2">
                    <div data-testid="payments-filter-container" className="relative">
                        <button
                            data-testid="filter-button"
                            ref={filterButtonRef}
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="group flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            aria-label={t('paymentpage.filter')}
                            aria-expanded={isFilterOpen}
                        >
                            <SlidersHorizontal className="w-5 h-5" />
                            {/* {t('paymentpage.filter')} */}
                        </button>

                        {isFilterOpen && (
                            <div ref={filterRef}>
                                <PaymentFilterPanel
                                    tempFilters={tempFilters}
                                    onTempFiltersChange={setTempFilters}
                                    dateErrors={dateErrors}
                                    onDateErrorsChange={setDateErrors}
                                    onApply={handleApplyFilters}
                                    onClear={clearFilters}
                                    patientOptions={patientOptions}
                                    userRole={userRole}
                                    isLoading={isLoading}
                                    t={t}
                                />
                            </div>
                        )}
                    </div>
                    <button
                        onClick={handleDownload}
                        disabled={downloadLoading || isLoading || (paymentsMetadata?.total === 0)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                        <Download className="w-4 h-4" />
                        {downloadLoading ? (t('paymentpage.downloading') || 'Downloading...') : (t('paymentpage.download_excel') || 'Download Excel')}
                    </button>
                </div>
            </div>

            {/* ---- Applied Filters Display ---- */}
            {(filters.startDate || filters.endDate || filters.mode.length > 0 || filters.status.length > 0 || filters.patientId.length > 0) && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2">
                            {t('paymentpage.applied_filters', 'Applied Filters')}:
                        </span>

                        {filters.startDate && (
                            <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('paymentpage.start_date', 'Start Date')}:</span> {new Date(filters.startDate).toLocaleDateString()}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, startDate: '' })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}

                        {filters.endDate && (
                            <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('paymentpage.end_date', 'End Date')}:</span> {new Date(filters.endDate).toLocaleDateString()}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, endDate: '' })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}

                        {filters.mode.map((m) => (
                            <div key={`mode-${m}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('paymentpage.payment_mode', 'Payment Mode')}:</span> {t(`paymentpage.payment_mode_${m.toLowerCase()}`)}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, mode: filters.mode.filter((x) => x !== m) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        {filters.status.map((s) => (
                            <div key={`status-${s}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('paymentpage.status')}:</span> {t(`paymentpage.payment_status_${s}`)}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, status: filters.status.filter((x) => x !== s) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        {filters.patientId.map((pid) => (
                            <div key={`patient-${pid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('paymentpage.patient', 'Patient')}:</span> {patientOptions.find(p => p.id === pid)?.name || '-'}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, patientId: filters.patientId.filter((x) => x !== pid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        <button
                            onClick={clearFilters}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                        >
                            <X className="h-3.5 w-3.5" />
                            {t('paymentpage.clear_all', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}

            {/* Table for larger screens, Cards for mobile */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('paymentpage.loading')}</span>
                </div>
            ) : (
                <>
                    {/* Table for sm and above */}
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="payments-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr data-testid="table-header" className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th data-testid="serial-no-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('paymentpage.serial_no')}</th>
                                    <th data-testid="price-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('paymentpage.price')}</th>
                                    {(userRole === 'doctor' || userRole === 'admin') && (<th data-testid="pricetype-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('paymentpage.pricetype')}</th>)}
                                    <th data-testid="payment-mode-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('paymentpage.payment_mode')}</th>
                                    <th data-testid="status-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('paymentpage.status')}</th>
                                    <th data-testid="date-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('paymentpage.date')}</th>
                                    <th data-testid="action-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('paymentpage.action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="payments-table-body">
                                {currentPayments.length > 0 ? (
                                    currentPayments.map((payment, index) => (
                                        <tr key={payment.id} data-testid={`payment-row-${payment.id}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                            <td data-testid={`payment-serial-${payment.id}`} className="p-3 text-xs  text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{((currentPage - 1) * itemsPerPage) + index + 1}</td>
                                            <td data-testid={`payment-price-${payment.id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{getCurrencySymbol(payment.currency)} {payment.price.toFixed(2)}</td>
                                            {(userRole === 'doctor' || userRole === 'admin') && (<td data-testid={`payment-pricetype-${payment.id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{payment?.pricetype === 'Type 1' ? t('paymentpage.pricetype_type_1') : payment?.pricetype === 'Type 2' ? t('paymentpage.pricetype_type_2') : t('paymentpage.pricetype_type_3')}</td>)}
                                            <td data-testid={`payment-mode-${payment.id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{payment.mode ? t(`paymentpage.payment_mode_${payment.mode.toLowerCase()}`, payment.mode) : t('paymentpage.na')}</td>
                                            <td data-testid={`payment-status-${payment.id}`} className="p-3 text-xs">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20">
                                                    {t(`paymentpage.payment_status_${payment.status}`)}
                                                </span>
                                            </td>
                                            <td data-testid={`payment-date-${payment.id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{new Date(payment.date).toLocaleDateString()}</td>
                                            <td data-testid={`payment-actions-${payment.id}`} className="p-3 text-xs flex justify-start gap-2 ">
                                                <button
                                                    data-testid={`view-payment-${payment.id}`}
                                                    onClick={() => handleViewDetails(payment)}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Eye data-testid={`view-icon-${payment.id}`} className="h-4 w-4 relative z-10" />
                                                </button>
                                                {(userRole === 'doctor' || userRole === 'admin') && (
                                                    isPaymentPaid(payment.status) ? (
                                                        <button
                                                            data-testid={`mark-not-paid-${payment.id}`}
                                                            onClick={() => handleTogglePaid(payment, false)}
                                                            title={t('paymentpage.mark_not_paid', 'Mark as not paid')}
                                                            className="group/btn text-amber-600 hover:text-white p-2 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-500 hover:to-amber-600 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <Undo2 data-testid={`mark-not-paid-icon-${payment.id}`} className="h-4 w-4 relative z-10" />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            data-testid={`mark-collected-${payment.id}`}
                                                            onClick={() => handleTogglePaid(payment, true)}
                                                            title={t('paymentpage.mark_collected', 'Mark collected')}
                                                            className="group/btn text-green-600 hover:text-white p-2 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-500 hover:to-emerald-600 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <Check data-testid={`mark-collected-icon-${payment.id}`} className="h-4 w-4 relative z-10" />
                                                        </button>
                                                    )
                                                )}
                                                {/* <button
                                                    data-testid={`view-receipt-${payment.id}`}
                                                    onClick={() => handleViewReceipt(payment)}
                                                    className="group/btn text-blue-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-500 hover:to-blue-600 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <FaFileInvoice data-testid={`receipt-icon-${payment.id}`} className="h-4 w-4 relative z-10" />
                                                </button> */}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr data-testid="no-payments-row-desktop">
                                        <td data-testid="no-payments-message" colSpan={(userRole === 'doctor' || userRole === 'admin') ? 7 : 6} className="p-3 text-center text-gray-500">
                                            {t('paymentpage.no_payments_found')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Card layout for mobile screens */}
                    <div className="block sm:hidden space-y-4">
                        {currentPayments.length > 0 ? (
                            currentPayments.map((payment, index) => (
                                <div
                                    key={payment.id}
                                    data-testid={`payment-card-${payment.id}`}
                                    className="p-4 bg-gradient-to-r bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <span data-testid={`payment-serial-${payment.id}`} className="text-xs font-bold text-[#4ABDBD]">
                                            {t('paymentpage.serial_no')}: {((currentPage - 1) * itemsPerPage) + index + 1}
                                        </span>
                                        <div className="flex gap-2">
                                            <button
                                                data-testid={`view-payment-${payment.id}`}
                                                onClick={() => handleViewDetails(payment)}
                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Eye data-testid={`view-icon-${payment.id}`} className="h-4 w-4 relative z-10" />
                                            </button>
                                            {(userRole === 'doctor' || userRole === 'admin') && (
                                                isPaymentPaid(payment.status) ? (
                                                    <button
                                                        data-testid={`mark-not-paid-mobile-${payment.id}`}
                                                        onClick={() => handleTogglePaid(payment, false)}
                                                        title={t('paymentpage.mark_not_paid', 'Mark as not paid')}
                                                        className="group/btn text-amber-600 hover:text-white p-2 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-500 hover:to-amber-600 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                    >
                                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                        <Undo2 className="h-4 w-4 relative z-10" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        data-testid={`mark-collected-mobile-${payment.id}`}
                                                        onClick={() => handleTogglePaid(payment, true)}
                                                        title={t('paymentpage.mark_collected', 'Mark collected')}
                                                        className="group/btn text-green-600 hover:text-white p-2 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-500 hover:to-emerald-600 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                    >
                                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                        <Check className="h-4 w-4 relative z-10" />
                                                    </button>
                                                )
                                            )}
                                            {/* <button
                                                data-testid={`view-receipt-${payment.id}`}
                                                onClick={() => handleViewReceipt(payment)}
                                                className="group/btn text-blue-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-500 hover:to-blue-600 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <FaFileInvoice data-testid={`receipt-icon-${payment.id}`} className="h-4 w-4 relative z-10" />
                                            </button> */}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div data-testid={`payment-price-${payment.id}`} className="text-xs text-gray-600">
                                            <span className="font-bold">{t('paymentpage.price')}:</span> {getCurrencySymbol(payment.currency)} {payment.price.toFixed(2)}
                                        </div>
                                        {(userRole === 'doctor' || userRole === 'admin') && (
                                            <div data-testid={`payment-pricetype-${payment.id}`} className="text-xs text-gray-600">
                                                <span className="font-bold">{t('paymentpage.pricetype')}:</span> {payment?.pricetype === 'Type 1' ? t('paymentpage.pricetype_type_1') : payment?.pricetype === 'Type 2' ? t('paymentpage.pricetype_type_2') : t('paymentpage.pricetype_type_3')}
                                            </div>
                                        )}
                                        <div data-testid={`payment-mode-${payment.id}`} className="text-xs text-gray-600">
                                            <span className="font-bold">{t('paymentpage.payment_mode')}:</span> {payment.mode ? t(`paymentpage.payment_mode_${payment.mode.toLowerCase()}`, payment.mode) : t('paymentpage.na')}
                                        </div>
                                        <div data-testid={`payment-status-${payment.id}`} className="text-xs">
                                            <span className="font-bold">{t('paymentpage.status')}:</span>
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20 ml-1">
                                                {t(`paymentpage.payment_status_${payment.status}`)}
                                            </span>
                                        </div>
                                        <div data-testid={`payment-date-${payment.id}`} className="text-xs text-gray-600">
                                            <span className="font-bold">{t('paymentpage.date')}:</span> {new Date(payment.date).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div data-testid="no-payments-row-mobile" className="p-3 text-center text-gray-500">
                                {t('paymentpage.no_payments_found')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Pagination */}
            {
                !isLoading && (paymentsMetadata?.total || 0) > 0 && (
                    <Pagination
                        totalItems={paymentsMetadata?.total || 0}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        custombutton={paymentsMetadata?.total === 0 ? 'disable' : ''}
                    />
                )
            }

            {isDetailsModalOpen && selectedPayment && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="payment-details-modal-title"
                    id="payment-details-modal"
                    data-testid="modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="payment-details-modal-title"
                                data-testid="modal-title"
                            >
                                {t('paymentpage.payment_details')}
                            </h2>
                            <button
                                id="payment-details-modal-close-button"
                                onClick={() => setIsDetailsModalOpen(false)}
                                aria-label={t('paymentpage.close_details_aria')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <section className="mb-6">
                                <h3 className="text-base sm:text-lg font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-3 pb-2">
                                    {t('paymentpage.appointment_info')}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-gray-500">
                                    <div className="grid">
                                        <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.address_type')}</strong>
                                        <span className="text-sm text-gray-600">{selectedPayment.addressTypeName}</span>
                                    </div>
                                    <div className="grid">
                                        <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.mode')}</strong>
                                        <span className="text-sm text-gray-600">
                                            {selectedPayment.appointmentMode === 'onsite'
                                                ? t('paymentpage.onsite')
                                                : t('paymentpage.visio')}
                                        </span>
                                    </div>
                                    <div className="grid">
                                        <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.duration')}</strong>
                                        <span className="text-sm text-gray-600">{selectedPayment.duration} {t('paymentpage.minutes')}</span>
                                    </div>
                                    <div className="grid">
                                        <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.price')}</strong>
                                        <span className="text-sm text-gray-600">{getCurrencySymbol(selectedPayment.currency)} {selectedPayment.price.toFixed(2)}</span>
                                    </div>
                                    <div className="grid">
                                        <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.date')}</strong>
                                        <span className="text-sm text-gray-600">{new Date(selectedPayment.scheduledDate).toLocaleDateString()}</span>
                                    </div>
                                    <div className="grid">
                                        <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.start_time')}</strong>
                                        <span className="text-sm text-gray-600">{formatDateTime(selectedPayment.localTime)}</span>
                                    </div>
                                    {/* <div className="grid">
                                        <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.end_time')}</strong>
                                        <span className="text-sm text-gray-600">{selectedPayment.slotEndTime}</span>
                                    </div> */}
                                    <div className="grid">
                                        <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.status_details')}</strong>
                                        <span className="text-sm text-gray-600">
                                            {selectedPayment.statusDetails === 'Completed'
                                                ? t('paymentpage.completed')
                                                : selectedPayment.statusDetails}
                                        </span>
                                    </div>
                                </div>
                            </section>
                            {userRole === 'doctor' && (
                                <section className="mb-6">
                                    <h3 className="text-base sm:text-lg font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-3 pb-2">
                                        {t('paymentpage.patient_info')}
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-500">
                                        <div className="grid">
                                            <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.name')}</strong>
                                            <span className="text-sm text-gray-600">{selectedPayment.patientName} {selectedPayment.patientSurname}</span>
                                        </div>
                                    </div>
                                </section>
                            )}
                            <section>
                                <h3 className="text-base sm:text-lg font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-3 pb-2">
                                    {t('paymentpage.doctor_info')}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-500">
                                    <div className="grid">
                                        <strong className="text-sm font-semibold text-gray-700">{t('paymentpage.doctor_name')}</strong>
                                        <span className="text-sm text-gray-600">{selectedPayment.doctorName}</span>
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="payment-details-close-button"
                                onClick={() => setIsDetailsModalOpen(false)}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                data-testid="close-button"
                            >
                                {t('paymentpage.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}